import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import SeverityGraph from "./SeverityGraph";
import TopAgentsGraph from "./TopAgentsGraph";
import OverAllMitreGraph from "../components/OverAllMitreGraph";

import { getUpdatedTimeByGroup as getUpdatedTimeByGroupService } from "services/modules/soc/agent";

import { DatePicker, Space } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { useNotification } from "config/Notification";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { Typography, Dialog, DialogContent } from "@mui/material";

const { RangePicker } = DatePicker;

const OverallDashboard = () => {
  // React router
  const navigate = useNavigate();

  // Notification
  const { openNotification } = useNotification();

  dayjs.extend(customParseFormat);

  // State Variables
  const [timeStamp, setTimeStamp] = useState(null);
  const [agentsFound, setAgentsFound] = useState();
  const [startDateTime, setStartDateTime] = useState(new Date());
  const [endDateTime, setEndDateTime] = useState(new Date());
  const [maxValue, setMaxValue] = useState(null);

  // Redux Selectors
  const selectedCompanyContainer = useSelector(
    (state) => state.auth.companyData
  );
  const agentsContainer = useSelector((state) => state.socData.agents);

  // Method handles to fetch the timestamps based on group
  const fetchTimeStamp = async () => {
    try {
      const params = {
        group: selectedCompanyContainer?.soc_group,
        soc_env: selectedCompanyContainer?.soc_env,
      };
      const response = await getUpdatedTimeByGroupService(params);
      setTimeStamp(response?.alert_timestamp);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTimeStamp();
    if (agentsContainer?.data?.length === 0) {
      setAgentsFound(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let start, end;
    if (timeStamp) {
      end = convertToIST(new Date(timeStamp).toISOString());
      const parsedDate = new Date(timeStamp);

      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid Date:", timeStamp);
      }
      setMaxValue(parsedDate);
    } else {
      end = new Date();
      end.setHours(0, 0, 0, 0);
    }

    start = convertToIST(new Date(end.getTime() - 24 * 60 * 60 * 1000).toISOString()); // Set start to 24 hours before end

    setStartDateTime(start);
    setEndDateTime(end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStamp]);


  const convertToIST = (dateString) => {
    // Check if string includes an explicit offset (e.g., +, -)
    if (dateString.includes('+') || dateString.includes('-')) {
      // Parse as is and convert to IST, returning a Date object
      return dayjs(dateString).tz("Asia/Kolkata", true).toDate();
    } else {
      // Assume it's UTC if it has 'Z', convert to IST and return a Date object
      return dayjs.utc(dateString).tz("Asia/Kolkata", true).toDate();
    }
  };


  return (
    <div className="p-4 .loader-container ">
      <Typography variant="h5"> Overall Dashboard</Typography>
      <div className="row ps-4">
        <div
          className=" col-lg-4 col-md-6 col-sm-6 col-xs-12 mx-2"
          style={{ zIndex: "100" }}
        >
          <div className="w-full max-w-xl flex flex-row gap-4">
            <Space direction="vertical" size={12}>
              <RangePicker
                showTime
                value={[dayjs(startDateTime), dayjs(endDateTime)]}
                format="YYYY-MM-DD HH:mm:ss"
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    const start = convertToIST(dates[0].toDate().toISOString());
                    const end = convertToIST(dates[1].toDate().toISOString());

                    // Custom range validation
                    const diffInMs = end.getTime() - start.getTime();

                    if (diffInMs < 0) {
                      openNotification(
                        "Start date must be before end date",
                        "warning"
                      );
                      return;
                    }
                    setStartDateTime(start);
                    setEndDateTime(end);
                  }
                }}
                disabledDate={(current) => {
                  // Disable future dates beyond maxValue
                  if (!maxValue) return false;
                  return current && current > dayjs(maxValue.toString());
                }}
              />
            </Space>
          </div>
        </div>
      </div>

      <div className="row p-4 form-card">
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
          <SeverityGraph endDate={endDateTime} startDate={startDateTime} />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
          <TopAgentsGraph endDate={endDateTime} startDate={startDateTime} />
        </div>
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <OverAllMitreGraph endDate={endDateTime} startDate={startDateTime} />
        </div>
        {agentsFound === false && (
          <div className="blur-effect">
            <Dialog
              open={agentsFound === false}
              onClose={() => setAgentsFound(true)}
              fullWidth
              maxWidth="xs"
            >
              <DialogContent>
                <div className="row d-flex justify-content-center align-items-center">
                  <WarningAmberIcon fontSize="large" color="warning" />
                  <strong align="center">
                    {" "}
                    No Critical Alerts found in last 24 hours
                  </strong>
                </div>
                <div className="row d-flex justify-content-center mt-2">
                  <div className="col-lg-1 col-md-1 col-sm-3 col-xs-3 px-0">
                    <button
                      type="button"
                      class="btn btn-warning"
                      onClick={() => {
                        navigate("/soc/view-agents");
                      }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverallDashboard;
